import React, { useState, useCallback } from 'react';
import { Field, Formik } from 'formik';
import * as Yup from 'yup';
import { Trans } from '@lingui/macro';
import every from 'lodash/every';
import get from 'lodash/get';
import { Alert } from 'antd';

import { CandidatePositionRelation, CreateRevieweeInput } from 'types';
import { FormContainer, InputField } from 'components/Form';
import { EMAIL_SCHEMA, REQUIRED_MESSAGE, NAME_SCHEMA } from 'util/yup';

export type ShareFormValues = Pick<CreateRevieweeInput, 'firstName' | 'lastName' | 'workEmail'> & {
  company: string;
};

const ShareFormSchema = Yup.object().shape({
  firstName: NAME_SCHEMA.required(REQUIRED_MESSAGE),
  lastName: NAME_SCHEMA.required(REQUIRED_MESSAGE),
  workEmail: EMAIL_SCHEMA.required(REQUIRED_MESSAGE),
  company: Yup.string().required(REQUIRED_MESSAGE),
});

export interface SharedFormProps {
  information: ShareFormValues;
  onSubmit(values: ShareFormValues): void;
  onBack(): void;
  onNextStep(): void;
  appliedCompanies: CandidatePositionRelation[];
}

const SharedForm = ({
  information,
  onSubmit,
  onBack,
  onNextStep,
  appliedCompanies,
}: SharedFormProps): JSX.Element => {
  const [showError, setShowError] = useState(false);
  const onDismiss = useCallback(() => {
    setShowError(false);
  }, []);
  const formSubmit = useCallback(
    (values, actions) => {
      const allowSubmit = every(appliedCompanies, appliedCompany => {
        const whiteListedEmailDomain = get(
          appliedCompany,
          'position.company.whitelistedEmailDomain'
        );
        return (
          !whiteListedEmailDomain || !(values.workEmail || '').includes(whiteListedEmailDomain)
        );
      });
      if (allowSubmit) {
        onSubmit(values);
        onNextStep();
      } else setShowError(true);
      actions.setSubmitting(false);
    },
    [appliedCompanies, onNextStep, onSubmit]
  );

  return (
    <div className="shared-form">
      {showError && (
        <Alert
          className="error-toast tw-w-full"
          type="error"
          message={
            <Trans>
              The company you are trying to share with already has your reference report! No need to
              share at this time
            </Trans>
          }
          onClose={onDismiss}
          closable
        />
      )}
      <Formik initialValues={information} onSubmit={formSubmit} validationSchema={ShareFormSchema}>
        <FormContainer
          error={showError}
          primaryButtonText={<Trans>Continue</Trans>}
          secondaryButtonText={<Trans>Back</Trans>}
          secondaryActionProps={{ className: 'tw-flex-1' }}
          primaryActionProps={{ className: 'tw-flex-1' }}
          onSecondaryButtonClick={onBack}
        >
          <div className="tw-flex tw-flex-col tw-gap-3">
            <Field
              component={InputField}
              label={
                <Trans>
                  <span className="tw-text-sm">First Name</span>
                </Trans>
              }
              name="firstName"
            />
            <Field
              component={InputField}
              label={
                <Trans>
                  <span className="tw-text-sm">Last Name</span>
                </Trans>
              }
              name="lastName"
            />
            <Field
              component={InputField}
              label={
                <Trans>
                  <span className="tw-text-sm">Work Email</span>
                </Trans>
              }
              name="workEmail"
            />
            <Field
              component={InputField}
              label={
                <Trans>
                  <span className="tw-text-sm">Company</span>
                </Trans>
              }
              name="company"
            />
          </div>
        </FormContainer>
      </Formik>
    </div>
  );
};

export default SharedForm;
